/**
 * Firebase Authentication Utilities
 * Provides helpers for token refresh and permission error handling
 */

import { auth } from '@/config/firebase';
import { logger } from './logger';

/**
 * Retry a Firebase operation with automatic token refresh on permission errors
 * This handles cases where custom claims aren't properly loaded
 * 
 * @template T - Return type of the operation
 * @param operation - The operation to execute
 * @param operationName - Human-readable name for logging
 * @param maxRetries - Maximum number of retry attempts (default: 1)
 * @returns The result of the operation
 * 
 * @example
 * ```typescript
 * const result = await retryWithTokenRefresh(
 *   async () => updateDoc(docRef, data),
 *   'updateFirmProvider',
 *   1
 * );
 * ```
 */
export async function retryWithTokenRefresh<T>(
    operation: () => Promise<T>,
    operationName: string,
    maxRetries: number = 1
): Promise<T> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error: unknown) {
            lastError = error;

            // Check if this is a permission error that might be fixed by refreshing token.
            // Firestore/callable errors use 'permission-denied'; Firebase Storage uses
            // 'storage/unauthorized' — both indicate stale or missing role/firmId claims.
            const err = error as { code?: string; message?: string };
            const isPermissionError = err?.code === 'permission-denied' ||
                err?.code === 'storage/unauthorized' ||
                err?.message?.includes('Missing or insufficient permissions') ||
                err?.message?.includes('PERMISSION_DENIED') ||
                err?.message?.includes('storage/unauthorized');

            // If this is not a permission error, or we've exhausted retries, throw immediately
            if (!isPermissionError || attempt === maxRetries) {
                // Before throwing, log diagnostic info
                if (isPermissionError && auth.currentUser) {
                    try {
                        const idTokenResult = await auth.currentUser.getIdTokenResult();
                        logger.error(`${operationName}: Permission denied after ${attempt + 1} attempts. Diagnostic info:`, {
                            userId: auth.currentUser.uid,
                            userEmail: auth.currentUser.email,
                            claims: {
                                firmId: idTokenResult.claims?.firmId,
                                role: idTokenResult.claims?.role,
                                isAdmin: idTokenResult.claims?.isAdmin,
                            },
                            note: 'If firmId or role is missing/incorrect, user may need to log out and back in, or contact support to fix custom claims.',
                        });
                    } catch (diagError: unknown) {
                        logger.error(`${operationName}: Could not get diagnostic info`, diagError instanceof Error ? diagError : new Error(String(diagError)));
                    }
                }
                throw error;
            }

            const errAny = error as Record<string, unknown>;
            logger.warn(`${operationName} failed with permission error, attempting token refresh (attempt ${attempt + 1}/${maxRetries + 1})`, {
                errorCode: errAny?.code,
                errorMessage: errAny?.message,
                currentUserId: auth.currentUser?.uid,
            });

            try {
                // First try to fix custom claims (common issue for attorneys)
                logger.debug(`${operationName}: Starting claims fix attempt`);
                logger.info(`${operationName}: Attempting to fix custom claims before token refresh`);

                try {
                    logger.debug(`${operationName}: Importing firebase/functions`);
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    logger.debug(`${operationName}: Firebase functions import successful`);
                    logger.info(`${operationName}: Firebase functions import successful`);

                    const functions = getFunctions();
                    logger.debug(`${operationName}: Creating fixUserCustomClaims callable`);
                    const fixClaims = httpsCallable(functions, 'fixUserCustomClaims');
                    logger.debug(`${operationName}: Calling fixUserCustomClaims`);
                    logger.info(`${operationName}: fixUserCustomClaims function callable created`);

                    const result = await fixClaims();
                    logger.debug(`${operationName}: Received response from fixUserCustomClaims`);
                    logger.info(`${operationName}: fixUserCustomClaims function called successfully`);

                    const data = result.data as { success: boolean; message: string; claimsFixed?: boolean };
                    logger.debug(`${operationName}: Claims fix result:`, data);
                    logger.info(`${operationName}: Claims fix result:`, data);

                    if (data.success) {
                        logger.debug(`${operationName}: Claims ${data.claimsFixed ? 'fixed' : 'verified'}`);
                        logger.info(`${operationName}: Custom claims ${data.claimsFixed ? 'fixed' : 'verified'}, proceeding with token refresh`);
                    }
                } catch (claimsError: unknown) {
                    logger.warn(`${operationName}: Claims fix attempt failed, proceeding with token refresh only`, { error: claimsError });
                    try {
                        const idTokenResult = await auth.currentUser?.getIdTokenResult();
                        logger.debug(`${operationName}: Current claims before refresh:`, {
                            firmId: idTokenResult?.claims?.firmId,
                            role: idTokenResult?.claims?.role,
                            isAdmin: idTokenResult?.claims?.isAdmin,
                            hasClaims: !!(idTokenResult?.claims?.firmId && idTokenResult?.claims?.role),
                        });
                        logger.info(`${operationName}: Current claims before refresh:`, {
                            firmId: idTokenResult?.claims?.firmId,
                            role: idTokenResult?.claims?.role,
                            isAdmin: idTokenResult?.claims?.isAdmin,
                            hasClaims: !!(idTokenResult?.claims?.firmId && idTokenResult?.claims?.role),
                        });
                    } catch (claimsCheckError) {
                        logger.warn(`${operationName}: Could not check current claims`, {
                            error: claimsCheckError instanceof Error ? claimsCheckError.message : String(claimsCheckError),
                        });
                    }
                    // This could happen if functions aren't deployed yet
                }

                // Force token refresh to get latest custom claims
                if (auth.currentUser) {
                    // Force refresh the token
                    await auth.currentUser.getIdToken(true);

                    // Verify claims were updated
                    const idTokenResult = await auth.currentUser.getIdTokenResult(true);
                    const hasValidClaims = !!(idTokenResult.claims?.firmId && idTokenResult.claims?.role);

                    logger.info(`${operationName}: Token refreshed, claims verified:`, {
                        firmId: idTokenResult.claims?.firmId,
                        role: idTokenResult.claims?.role,
                        isAdmin: idTokenResult.claims?.isAdmin,
                        hasValidClaims,
                    });

                    if (!hasValidClaims) {
                        logger.warn(`${operationName}: Claims still missing after refresh. User may need to log out and back in.`);
                        // Still try the operation - sometimes there's a delay in propagation
                    }

                    // Small delay to allow claims to propagate to Firestore security rules
                    await new Promise(resolve => setTimeout(resolve, 500));

                    logger.info(`${operationName}: Retrying operation after token refresh`);
                } else {
                    throw new Error('No authenticated user found');
                }
            } catch (refreshError: unknown) {
                logger.error(`${operationName}: Failed to refresh token`, refreshError);
                throw error; // Throw original error, not refresh error
            }
        }
    }

    throw lastError;
}
